import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";

/**
 * Voice2Evidence™ Client Playbook — short-form scrollable guide for the
 * client project owner. Two-minute read. Generic visuals only — no
 * client-identifying screenshots. Standalone URL (`/playbook`), no
 * public-site navigation, no login. Layout: sticky left TOC, right column
 * of tight editorial + CSS/HTML mockups of each console pattern.
 */

const CHAPTERS = [
  { id: "positioning",  num: "01", label: "What Voice2Evidence™ is" },
  { id: "flow",         num: "02", label: "The six-stage flow" },
  { id: "signals",      num: "03", label: "Dual-lane signal sourcing" },
  { id: "triangulation",num: "04", label: "How signals converge" },
  { id: "teams",        num: "05", label: "What your teams get" },
  { id: "pab",          num: "06", label: "The Patient Advisory Brief" },
  { id: "kol",          num: "07", label: "The KOL Advisory Brief" },
  { id: "close",        num: "08", label: "Ready when you are" },
];

// ──────────────────────────────────────────────────────────────────────────
// Small primitives — match the Deck / Console typographic system.
// ──────────────────────────────────────────────────────────────────────────
const Eyebrow = ({ children }) => (
  <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#C84C21]">{children}</div>
);
const H2 = ({ children }) => (
  <h2 className="font-serif font-normal text-3xl md:text-[2.25rem] text-[#0F1825] leading-[1.1] tracking-tight">{children}</h2>
);
const Lede = ({ children }) => (
  <p className="text-[16.5px] leading-relaxed text-[#3F4856]">{children}</p>
);
const Chapter = ({ id, num, eyebrow, title, children }) => (
  <section id={id} data-testid={`playbook-${id}`} className="pt-20 pb-8 scroll-mt-8">
    <div className="mb-6 flex items-baseline gap-4">
      <span className="font-mono text-[52px] leading-none text-[#E8E1D2] tabular-nums">{num}</span>
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <div className="mt-2"><H2>{title}</H2></div>
      </div>
    </div>
    {children}
  </section>
);

// ──────────────────────────────────────────────────────────────────────────
// Mockup primitives — generic, client-agnostic visualisations that stand
// in for real screenshots. They mimic the console visual system so the
// reader recognises the pattern the moment they log in.
// ──────────────────────────────────────────────────────────────────────────

// PentaVoice mockup — the 5-stream lattice.
const PentaVoiceMockup = () => (
  <div className="border border-[#E8E1D2] bg-white p-5 max-w-[720px]" aria-hidden="true">
    <div className="grid grid-cols-5 gap-2.5">
      {[
        { k: "L1", label: "Digital & Social" },
        { k: "L2", label: "PROs & Surveys" },
        { k: "L3", label: "Patient Advisory" },
        { k: "L4", label: "RWD / RWE" },
        { k: "L5", label: "Regulatory / HTA" },
      ].map((s) => (
        <div key={s.k} className="text-center py-3 border border-[#E8E1D2] bg-[#FAF7F1]">
          <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#C84C21]">{s.k}</div>
          <div className="mt-1.5 text-[11.5px] font-serif text-[#0F1825] leading-tight px-1">{s.label}</div>
        </div>
      ))}
    </div>
    <div className="mt-4 font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#A09680] text-center">
      Five streams · One framework · Converged into insight
    </div>
  </div>
);

// Six-stage flow mockup — the workspace tab order.
const FlowMockup = () => {
  const stages = [
    ["01", "Engagement Brief",      "Frame it"],
    ["02", "Source Library",         "Stage inputs"],
    ["03", "Signal Intake",          "Extract & tag"],
    ["04", "Concept Map",            "Cross-walk"],
    ["05", "Triangulation View",     "Synthesise"],
    ["06", "Team Recommendations",   "Ship it"],
  ];
  return (
    <div className="border border-[#E8E1D2] bg-white p-5 max-w-[720px]" aria-hidden="true">
      <ol className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
        {stages.map(([n, tab, verb]) => (
          <li key={n} className="border border-[#E8E1D2] bg-[#FAF7F1] p-3">
            <div className="flex items-baseline justify-between mb-1">
              <span className="font-mono text-[10px] tabular-nums text-[#C84C21]">{n}</span>
              <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#A09680]">{verb}</span>
            </div>
            <div className="font-serif text-[13.5px] text-[#0F1825] leading-snug">{tab}</div>
          </li>
        ))}
      </ol>
    </div>
  );
};

// Triangulation strength mockup — one sub-theme card with strength ribbon.
const TriangulationCardMockup = () => (
  <div className="border border-[#E8E1D2] bg-white max-w-[560px]" aria-hidden="true">
    <div className="flex items-center gap-2 border-b border-[#E8E1D2] px-4 py-2.5">
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#A09680]">Sub-theme</span>
      <span className="text-[#E8E1D2]">·</span>
      <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#0F1825]">C1 · Treatment decisions</span>
      <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.16em] text-white bg-[#3F6D55] px-2 py-[3px]">Confirmed · Strong</span>
    </div>
    <div className="px-4 py-3.5">
      <p className="font-serif text-[13.5px] text-[#0F1825] leading-snug">
        Independent evidence from all five streams converges on a single patient-decision pattern, meeting the Confirmed · Strong threshold.
      </p>
      <div className="mt-3 flex items-center gap-1.5">
        {["L1", "L2", "L3", "L4", "L5"].map((s) => (
          <span key={s} className="font-mono text-[9.5px] uppercase tracking-[0.14em] bg-[#FAF7F1] border border-[#C84C21]/30 text-[#C84C21] px-1.5 py-[1px]">{s}</span>
        ))}
      </div>
    </div>
  </div>
);

// Team Recommendations 3-column row mockup — the deliverable pattern.
const TeamRowMockup = () => (
  <div className="border border-[#E8E1D2] bg-white max-w-[860px]" aria-hidden="true">
    <div className="grid grid-cols-12 border-b border-[#E8E1D2] bg-[#FAF7F1]">
      <div className="col-span-4 px-4 py-2 font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#5C6470]">Key patient theme</div>
      <div className="col-span-5 px-4 py-2 font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#5C6470] border-l border-[#E8E1D2]">Team recommendation</div>
      <div className="col-span-3 px-4 py-2 font-mono text-[9.5px] uppercase tracking-[0.18em] text-[#5C6470] border-l border-[#E8E1D2]">References</div>
    </div>
    <div className="grid grid-cols-12">
      <div className="col-span-4 px-4 py-4">
        <div className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#A09680] mb-1.5">C1 · Treatment decisions</div>
        <p className="font-serif text-[13px] text-[#0F1825] leading-snug">
          Patients describe treatment choice as a trade-off they lack the language to voice inside the exam room.
        </p>
      </div>
      <div className="col-span-5 px-4 py-4 border-l border-[#E8E1D2]">
        <div className="mb-1.5 flex gap-1">
          <span className="font-mono text-[9px] uppercase tracking-[0.14em] bg-[#0F1825] text-white px-1.5 py-[2px]">Medical Affairs</span>
          <span className="font-mono text-[9px] uppercase tracking-[0.14em] bg-[#0F1825] text-white px-1.5 py-[2px]">HEOR</span>
        </div>
        <p className="text-[12.5px] text-[#0F1825] leading-relaxed">
          Prioritise decision-support materials that name the trade-offs explicitly and equip HCPs to open the conversation.
        </p>
      </div>
      <div className="col-span-3 px-4 py-4 border-l border-[#E8E1D2]">
        <ol className="space-y-1.5 text-[12px]">
          <li className="flex gap-1.5"><span className="font-mono text-[10px] text-[#C84C21] tabular-nums">[1]</span><span className="text-[#0F1825] underline decoration-[#C84C21]/40">Peer-reviewed cohort study</span></li>
          <li className="flex gap-1.5"><span className="font-mono text-[10px] text-[#C84C21] tabular-nums">[2]</span><span className="text-[#0F1825] underline decoration-[#C84C21]/40">Advocacy focus-group report</span></li>
          <li className="flex gap-1.5"><span className="font-mono text-[10px] text-[#C84C21] tabular-nums">[3]</span><span className="text-[#0F1825] underline decoration-[#C84C21]/40">PRO instrument publication</span></li>
        </ol>
      </div>
    </div>
  </div>
);

// PAB question type mockup — three tags.
const PabTagMockup = () => (
  <div className="border border-[#E8E1D2] bg-white p-5 max-w-[680px]" aria-hidden="true">
    <div className="grid grid-cols-3 gap-3">
      {[
        ["Confirm", "#3F6D55", "Validate a strong desk finding with patients in the room."],
        ["Gap-probe", "#B36A00", "Close a named evidence gap with primary patient input."],
        ["Tension", "#7B4CC4", "Surface a trade-off only patients can weight."],
      ].map(([tag, color, body]) => (
        <div key={tag}>
          <div className="font-mono text-[10px] uppercase tracking-[0.16em] px-2 py-[3px] inline-block text-white" style={{ backgroundColor: color }}>{tag}</div>
          <p className="mt-2 text-[12.5px] text-[#3F4856] leading-relaxed">{body}</p>
        </div>
      ))}
    </div>
  </div>
);

// KOL question type mockup — pre-launch physician board, three tags tied to
// real pharma launch decisions.
const KolTagMockup = () => (
  <div className="border border-[#E8E1D2] bg-white p-5 max-w-[720px]" aria-hidden="true">
    <div className="grid grid-cols-3 gap-3">
      {[
        ["Positioning", "#22456A", "Where does the asset sit? Which patient does the KOL reach for first?", "Launch statement · Patient ID · HTA narrative"],
        ["Adoption",    "#8C3A12", "What evidence / operational / competitive barriers block first prescription?", "MSL narrative · Publications · Sales training"],
        ["Advocacy",    "#4E378A", "What data or role would earn the KOL a public-champion voice?", "KOL roadmap · RWE plan · Symposium series"],
      ].map(([tag, color, body, feeds]) => (
        <div key={tag}>
          <div className="font-mono text-[10px] uppercase tracking-[0.16em] px-2 py-[3px] inline-block text-white" style={{ backgroundColor: color }}>{tag}</div>
          <p className="mt-2 text-[12.5px] text-[#3F4856] leading-relaxed">{body}</p>
          <div className="mt-2 font-mono text-[9.5px] uppercase tracking-[0.14em] text-[#A09680]">Feeds → {feeds}</div>
        </div>
      ))}
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────
export default function Playbook() {
  const [activeId, setActiveId] = useState("positioning");
  const observerRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.backgroundColor;
    document.body.style.backgroundColor = "#FAF7F1";
    document.documentElement.style.backgroundColor = "#FAF7F1";
    return () => {
      document.body.style.backgroundColor = prev;
      document.documentElement.style.backgroundColor = prev;
    };
  }, []);

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        setActiveId(visible[0].target.id);
      },
      { rootMargin: "-15% 0px -60% 0px", threshold: [0.1, 0.25, 0.5] },
    );
    for (const c of CHAPTERS) {
      const el = document.getElementById(c.id);
      if (el) observerRef.current.observe(el);
    }
    return () => observerRef.current?.disconnect();
  }, []);

  const scrollToChapter = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const activeChapter = useMemo(
    () => CHAPTERS.find((c) => c.id === activeId) || CHAPTERS[0],
    [activeId],
  );

  return (
    <div className="min-h-screen bg-[#FAF7F1] text-[#0F1825]" data-testid="page-playbook">
      {/* Sticky top bar */}
      <header className="sticky top-0 z-40 bg-[#FAF7F1]/95 backdrop-blur border-b border-[#E8E1D2]">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.22em] text-[#5C6470] hover:text-[#C84C21] transition-colors"
            data-testid="playbook-back-home"
          >
            <ArrowLeft size={12} />
            Voice2Evidence™
          </Link>
          <div className="hidden md:flex items-baseline gap-3">
            <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[#A09680]">Client Playbook</span>
            <span className="text-[#E8E1D2]">·</span>
            <span className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[#0F1825]">
              {activeChapter.num} · {activeChapter.label}
            </span>
          </div>
          <Link
            to="/console/login"
            className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-white bg-[#0F1825] hover:bg-[#C84C21] transition-colors px-3 py-2"
            data-testid="playbook-open-console"
          >
            Open workspace
            <ChevronRight size={12} />
          </Link>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-12 gap-12">
        {/* Sticky TOC */}
        <aside className="hidden lg:block col-span-3">
          <nav className="sticky top-24" data-testid="playbook-toc">
            <Eyebrow>On this page</Eyebrow>
            <ol className="mt-4 space-y-1">
              {CHAPTERS.map((c) => {
                const isActive = c.id === activeId;
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => scrollToChapter(c.id)}
                      data-testid={`playbook-toc-${c.id}`}
                      className={`w-full text-left flex items-baseline gap-3 py-1.5 border-l-2 pl-4 transition-colors ${
                        isActive
                          ? "border-[#C84C21] text-[#0F1825]"
                          : "border-transparent text-[#5C6470] hover:text-[#0F1825]"
                      }`}
                    >
                      <span className="font-mono text-[10.5px] tabular-nums shrink-0">{c.num}</span>
                      <span className="text-[13px] leading-snug">{c.label}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <div className="mt-8 pt-6 border-t border-[#E8E1D2]">
              <p className="text-[12px] leading-relaxed text-[#5C6470]">
                Two-minute read. Everything you need to know before your first log-in.
              </p>
            </div>
          </nav>
        </aside>

        {/* Main content */}
        <main className="col-span-12 lg:col-span-9 max-w-[820px]">
          {/* ── 01 · POSITIONING ─────────────────────────────────────── */}
          <Chapter id="positioning" num="01" eyebrow="Positioning" title="Patient voice, decision-grade.">
            <Lede>
              Voice2Evidence™ turns lived patient experience into evidence your teams can act on. Five patient-voice streams, one framework, one deliverable — synthesised by our methodology team, delivered inside your workspace.
            </Lede>
            <div className="mt-6"><PentaVoiceMockup /></div>
          </Chapter>

          {/* ── 02 · FLOW ────────────────────────────────────────────── */}
          <Chapter id="flow" num="02" eyebrow="The workflow" title="Six stages. One closed loop.">
            <Lede>
              Every engagement moves through the same six stages. Each has its own tab in your workspace and ships a defined deliverable at its own stage boundary.
            </Lede>
            <div className="mt-6"><FlowMockup /></div>
          </Chapter>

          {/* ── 03 · SIGNALS ─────────────────────────────────────────── */}
          <Chapter id="signals" num="03" eyebrow="Signal sourcing" title="Two lanes. One truth.">
            <Lede>
              Every stream is fed by two independent extraction lanes. Convergence between the two is what makes the evidence trustworthy — one lane alone is never enough.
            </Lede>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-[720px]">
              <div className="border border-[#E8E1D2] bg-white p-5">
                <Eyebrow>Lane A</Eyebrow>
                <div className="mt-2 font-serif text-[16px] text-[#0F1825] leading-snug">Real-time patient voice</div>
                <p className="mt-2 text-[13px] text-[#3F4856] leading-relaxed">
                  Deep-research pass across public patient channels, advocacy statements, congress narratives, HTA rationales.
                </p>
              </div>
              <div className="border border-[#E8E1D2] bg-white p-5">
                <Eyebrow>Lane B</Eyebrow>
                <div className="mt-2 font-serif text-[16px] text-[#0F1825] leading-snug">Peer-reviewed evidence</div>
                <p className="mt-2 text-[13px] text-[#3F4856] leading-relaxed">
                  Semantic Scholar · Europe PMC · Crossref · ClinicalTrials.gov — deduped against Lane A by DOI, URL, and title.
                </p>
              </div>
            </div>
          </Chapter>

          {/* ── 04 · TRIANGULATION ───────────────────────────────────── */}
          <Chapter id="triangulation" num="04" eyebrow="Triangulation" title="Signals become insights.">
            <Lede>
              Every V2E sub-theme becomes a Triangulated Insight only when independent streams agree. Convergence is measured, labelled, and made visible on the map.
            </Lede>
            <div className="mt-6"><TriangulationCardMockup /></div>
            <ul className="mt-5 space-y-1.5 max-w-[560px] text-[13.5px]">
              <li className="flex items-baseline gap-3">
                <span className="w-24 font-mono text-[10px] uppercase tracking-[0.14em] text-[#3F6D55]">Strong</span>
                <span className="text-[#3F4856]">≥ 3 streams agree.</span>
              </li>
              <li className="flex items-baseline gap-3">
                <span className="w-24 font-mono text-[10px] uppercase tracking-[0.14em] text-[#B36A00]">Emerging</span>
                <span className="text-[#3F4856]">2 streams agree.</span>
              </li>
              <li className="flex items-baseline gap-3">
                <span className="w-24 font-mono text-[10px] uppercase tracking-[0.14em] text-[#5C6470]">Single-source</span>
                <span className="text-[#3F4856]">1 stream. Held as narrative context.</span>
              </li>
              <li className="flex items-baseline gap-3">
                <span className="w-24 font-mono text-[10px] uppercase tracking-[0.14em] text-[#B83A28]">Absence</span>
                <span className="text-[#3F4856]">Zero streams. Silence is data.</span>
              </li>
            </ul>
          </Chapter>

          {/* ── 05 · TEAMS ───────────────────────────────────────────── */}
          <Chapter id="teams" num="05" eyebrow="The deliverable" title="One row. One team action.">
            <Lede>
              Team Recommendations turns every insight into a per-team action. One row per patient theme, mapped to the function that owns it, with every claim traced back to a real, hyperlinked reference.
            </Lede>
            <div className="mt-6"><TeamRowMockup /></div>
            <div className="mt-6 flex flex-wrap gap-2 max-w-[720px]">
              {["Medical Affairs", "HEOR", "Market Access", "Patient Engagement", "Regulatory"].map((t) => (
                <span key={t} className="font-mono text-[10px] uppercase tracking-[0.14em] bg-white border border-[#E8E1D2] text-[#0F1825] px-2 py-1">{t}</span>
              ))}
            </div>
          </Chapter>

          {/* ── 06 · PAB ─────────────────────────────────────────────── */}
          <Chapter id="pab" num="06" eyebrow="Patient Advisory Brief" title="A discussion guide, on tap.">
            <Lede>
              When triangulation is locked, the workspace produces a moderator-ready patient advisory brief — questions tagged by intent, each traceable to a specific sub-theme finding.
            </Lede>
            <div className="mt-6"><PabTagMockup /></div>
          </Chapter>

          {/* ── 07 · KOL ─────────────────────────────────────────────── */}
          <Chapter id="kol" num="07" eyebrow="KOL Advisory Brief" title="The physician board, pre-launch.">
            <Lede>
              A sibling brief tuned for the pre-launch KOL board. Same evidence base as the Patient Advisory Brief, but every question is framed for a senior clinician and mapped to one of three pharma launch decisions. Generated on demand from the triangulated findings — no re-triangulation needed.
            </Lede>
            <div className="mt-6"><KolTagMockup /></div>
            <div className="mt-6 max-w-[720px] text-[13.5px] text-[#3F4856] leading-relaxed">
              <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[#C84C21]">Same 5 themes · 40 questions</span>
              <p className="mt-2">Clinical language a senior physician would use in their own practice — never marketing-toned. Every question probes how the KOL <span className="italic">reasons</span> about a launch-stage decision rather than what they already know.</p>
            </div>
          </Chapter>

          {/* ── 08 · CLOSE ───────────────────────────────────────────── */}
          <Chapter id="close" num="08" eyebrow="Get started" title="Ready when you are.">
            <Lede>
              Log in to open your workspace, or reach out to walk through a pilot engagement with the methodology team.
            </Lede>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <Link
                to="/console/login"
                className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-white bg-[#0F1825] hover:bg-[#C84C21] transition-colors px-4 py-3"
                data-testid="playbook-cta-open-console"
              >
                Open your workspace
                <ChevronRight size={12} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-[#0F1825] border border-[#0F1825] hover:bg-[#0F1825] hover:text-white transition-colors px-4 py-3"
                data-testid="playbook-cta-contact"
              >
                Talk to the methodology team
              </Link>
            </div>
          </Chapter>

          <footer className="mt-16 border-t border-[#E8E1D2] pt-6 pb-16 flex items-center justify-between flex-wrap gap-4">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[#A09680]">
              Voice2Evidence™ · Client Playbook
            </div>
            <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[#5C6470]">
              Two-minute read · Confidential
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
}
